/**
 * BridgeConfig — Configuração do Servidor Ponte OAuth → API Key
 * Integrado como tab no ElonRouter
 */

import { useState, useEffect } from 'preact/hooks';
import { api } from '../api/client';
import { SkeletonBar, Button, Input, Select, Textarea } from '../components/ui';
import { T } from '../styles/tokens';

// ═══ Design Tokens ═══
const theme = {
  bg: T.bg,
  card: T.bg,
  border: T.borderSubtle,
  text: T.text,
  dimmed: T.textMuted,
  accent: T.accent,
  success: T.success,
  error: T.danger,
  warning: T.warning,
  input: T.bgInput,
};

interface BridgeConfigData {
  enabled: boolean;
  ip: string;
  port: number;
  ssh_port: number;
  ssh_user: string;
  ssh_auth_type: 'password' | 'key';
  ssh_password: string;
  ssh_key: string;
  anthropic_api_key: string;
  last_health_check: string | null;
  health_status: 'ok' | 'failed' | 'unknown';
}

const DEFAULT_CONFIG: BridgeConfigData = {
  enabled: false,
  ip: '',
  port: 3000,
  ssh_port: 22,
  ssh_user: 'root',
  ssh_auth_type: 'password',
  ssh_password: '',
  ssh_key: '',
  anthropic_api_key: '',
  last_health_check: null,
  health_status: 'unknown',
};

export function BridgeTab() {
  const [config, setConfig] = useState<BridgeConfigData>(DEFAULT_CONFIG);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const [statusMsg, setStatusMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [serverInfo, setServerInfo] = useState<any>(null);

  useEffect(() => {
    loadConfig();
    loadServerInfo();
    const interval = setInterval(loadServerInfo, 10000);
    return () => clearInterval(interval);
  }, []);

  async function loadServerInfo() {
    try {
      const res = await api.get('/api/v1/admin/bridge/server-info');
      setServerInfo(res.data);
    } catch (e) {
      console.error('Failed to load server info:', e);
    }
  }

  async function loadConfig() {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get('/api/v1/admin/bridge/config');
      if (res.data?.config) {
        setConfig({ ...DEFAULT_CONFIG, ...res.data.config });
      }
    } catch (e: any) {
      console.error('Failed to load bridge config:', e);
      setError(e.message || 'Falha ao carregar configuração');
      // Still use defaults on error
    } finally {
      setLoading(false);
    }
  }

  async function saveConfig() {
    setSaving(true);
    setStatusMsg(null);
    try {
      await api.put('/api/v1/admin/bridge/config', config);
 setStatusMsg({ type: 'success', text: 'Configuração salva!' });
    } catch (e: any) {
 setStatusMsg({ type: 'error', text: `Erro: ${e.message}` });
    }
    setSaving(false);
  }

  async function testHealth() {
    setTesting('health');
    setStatusMsg(null);
    try {
      const res = await api.post('/api/v1/admin/bridge/health', { ip: config.ip, port: config.port });
      if (res.data?.status === 'ok') {
        setConfig(c => ({ ...c, health_status: 'ok', last_health_check: new Date().toISOString() }));
 setStatusMsg({ type: 'success', text: `Health OK! Uptime: ${res.data.uptime}s` });
      } else {
        setConfig(c => ({ ...c, health_status: 'failed' }));
 setStatusMsg({ type: 'error', text: 'Health check falhou' });
      }
    } catch (e: any) {
      setConfig(c => ({ ...c, health_status: 'failed' }));
 setStatusMsg({ type: 'error', text: `Erro: ${e.message}` });
    }
    setTesting(null);
  }

  async function testSSH() {
    setTesting('ssh');
    setStatusMsg(null);
    try {
      const res = await api.post('/api/v1/admin/bridge/ssh-test', {
        ip: config.ip, ssh_port: config.ssh_port,
        ssh_user: config.ssh_user, ssh_auth_type: config.ssh_auth_type,
        ssh_password: config.ssh_password, ssh_key: config.ssh_key,
      });
 setStatusMsg({ type: 'success', text: `SSH conectou! ${res.data?.message || ''}` });
    } catch (e: any) {
 setStatusMsg({ type: 'error', text: `SSH falhou: ${e.message}` });
    }
    setTesting(null);
  }

  async function deployRemote() {
    setTesting('deploy');
    setStatusMsg(null);
    try {
      const res = await api.post('/api/v1/admin/bridge/deploy', config);
 setStatusMsg({ type: 'success', text: `Deploy iniciado! ${res.data?.message || ''}` });
    } catch (e: any) {
 setStatusMsg({ type: 'error', text: `Deploy falhou: ${e.message}` });
    }
    setTesting(null);
  }

  const inputStyle = {
    width: '100%',
    padding: '10px 14px',
    background: theme.input,
    border: `1px solid ${theme.border}`,
    borderRadius: 8,
    color: theme.text,
    fontSize: 14,
    outline: 'none',
    boxSizing: 'border-box' as const,
  };

  const labelStyle = {
    display: 'block',
    marginBottom: 6,
    fontSize: 13,
    color: theme.dimmed,
    fontWeight: 500 as const,
  };

  const btnStyle = (color: string, disabled?: boolean) => ({
    padding: '10px 20px',
    background: disabled ? theme.border : color,
    color: T.text,
    border: 'none',
    borderRadius: 8,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 13,
    fontWeight: 600 as const,
    opacity: disabled ? 0.5 : 1,
  });

  // Loading state
  if (loading) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{ textAlign: 'center', color: theme.dimmed }}>
 <div style={{ fontSize: 24, marginBottom: 12 }}></div>
          <div style={{ padding: 12 }}><SkeletonBar width="60%" height={14} style={{ marginBottom: 8 }} /><SkeletonBar width="40%" height={14} /></div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <div style={{
          padding: '16px 20px',
          background: T.dangerBg,
          border: `1px solid ${theme.error}`,
          borderRadius: 12,
          color: theme.error,
        }}>
 <h3 style={{ margin: '0 0 8px', fontSize: 16 }}>Erro ao carregar</h3>
          <p style={{ margin: 0, fontSize: 13 }}>{error}</p>
          <Button variant="danger" size="sm" onClick={() => loadConfig()} style={{ marginTop: 12 }}>
 Tentar novamente
          </Button>
        </div>
      </div>
    );
  }

 const statusIcon = serverInfo?.status === 'online' ? '' : serverInfo?.status === 'offline' ? '' : '';
  const statusColor = serverInfo?.status === 'online' ? theme.success : serverInfo?.status === 'offline' ? theme.error : theme.warning;

  return (
    <div>
      {/* Server Info Card */}
      {serverInfo && (
        <div style={{
          padding: 20, background: theme.card, border: `1px solid ${theme.border}`,
          borderRadius: 12, marginBottom: 24,
        }}>
          <h3 style={{ margin: '0 0 16px', fontSize: 16, color: theme.text }}>
 Server-Ponte OAuth
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 }}>
            {[
              { label: 'Status', value: `${statusIcon} ${serverInfo.status}`, color: statusColor },
              { label: 'IP', value: serverInfo.ip },
              { label: 'Hostname', value: serverInfo.hostname },
              { label: 'Latência', value: serverInfo.health?.latency_ms != null ? `${serverInfo.health.latency_ms}ms` : 'N/A' },
              { label: 'Versão', value: serverInfo.version || 'N/A' },
              { label: 'Edges CF', value: `${serverInfo.cloudflare_edges} connected` },
            ].map((item, i) => (
              <div key={i} style={{ padding: '8px 12px', background: theme.input, borderRadius: 8 }}>
                <div style={{ fontSize: 11, color: theme.dimmed, marginBottom: 4 }}>{item.label}</div>
                <div style={{ fontSize: 14, color: item.color || theme.text, fontWeight: 500 }}>{item.value}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
            <Button size="sm" onClick={loadServerInfo}>
 Atualizar Status
            </Button>
            <Button variant="ghost" size="sm" onClick={() => window.open('/api/v1/bridge/health')}>
 Health Check
            </Button>
          </div>
        </div>
      )}

      {/* Header com toggle */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 24, padding: '16px 20px',
        background: theme.card, border: `1px solid ${theme.border}`, borderRadius: 12,
      }}>
        <div>
 <h3 style={{ margin: 0, fontSize: 18, color: theme.text }}>Servidor Ponte OAuth</h3>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: theme.dimmed }}>
            Traduz OAuth tokens → API key real para Anthropic
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 12, color: theme.dimmed }}>
 {config.enabled ? 'Habilitado' : 'Desabilitado'}
          </span>
          <label style={{ position: 'relative', display: 'inline-block', width: 48, height: 26 }}>
            <input
              type="checkbox"
              checked={config.enabled}
              onChange={(e) => setConfig(c => ({ ...c, enabled: (e.target as HTMLInputElement).checked }))}
              style={{ opacity: 0, width: 0, height: 0 }}
            />
            <span style={{
              position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
              background: config.enabled ? theme.success : theme.border,
              borderRadius: 26, cursor: 'pointer', transition: 'background 0.3s',
            }}>
              <span style={{
                position: 'absolute', top: 3, left: config.enabled ? 25 : 3,
                width: 20, height: 20, background: '#fff', borderRadius: '50%',
                transition: 'left 0.3s',
              }} />
            </span>
          </label>
        </div>
      </div>

      {/* Status bar */}
      <div style={{
        display: 'flex', gap: 16, marginBottom: 24,
      }}>
        <div style={{
          flex: 1, padding: '12px 16px',
          background: theme.card, border: `1px solid ${theme.border}`, borderRadius: 10,
        }}>
          <span style={{ fontSize: 12, color: theme.dimmed }}>Health Status</span>
          <div style={{
            fontSize: 16, fontWeight: 600, marginTop: 4,
            color: config.health_status === 'ok' ? theme.success : config.health_status === 'failed' ? theme.error : theme.dimmed,
          }}>
 {config.health_status === 'ok' ? 'Online' : config.health_status === 'failed' ? 'Offline' : 'Desconhecido'}
          </div>
        </div>
        <div style={{
          flex: 1, padding: '12px 16px',
          background: theme.card, border: `1px solid ${theme.border}`, borderRadius: 10,
        }}>
          <span style={{ fontSize: 12, color: theme.dimmed }}>Último Health Check</span>
          <div style={{ fontSize: 14, marginTop: 4, color: theme.text }}>
            {config.last_health_check ? new Date(config.last_health_check).toLocaleString() : 'Nunca'}
          </div>
        </div>
      </div>

      {/* Config form */}
      <div style={{
        background: theme.card, border: `1px solid ${theme.border}`, borderRadius: 12,
        padding: 24, marginBottom: 24,
      }}>
 <h4 style={{ margin: '0 0 20px', color: theme.text, fontSize: 15 }}>Conexão do Servidor</h4>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>IP do Servidor</label>
            <Input
              placeholder="192.168.1.100"
              value={config.ip}
              onChange={(v) => setConfig(c => ({ ...c, ip: v }))}
            />
          </div>
          <div>
            <label style={labelStyle}>Porta do Bridge</label>
            <Input
              type="number"
              value={config.port}
              onChange={(v) => setConfig(c => ({ ...c, port: parseInt(v) || 3000 }))}
            />
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
          <div>
            <label style={labelStyle}>SSH User</label>
            <Input
              value={config.ssh_user}
              onChange={(v) => setConfig(c => ({ ...c, ssh_user: v }))}
            />
          </div>
          <div>
            <label style={labelStyle}>SSH Port</label>
            <Input
              type="number"
              value={config.ssh_port}
              onChange={(v) => setConfig(c => ({ ...c, ssh_port: parseInt(v) || 22 }))}
            />
          </div>
          <div>
            <label style={labelStyle}>Auth Type</label>
            <Select
              value={config.ssh_auth_type}
              onChange={(v) => setConfig(c => ({ ...c, ssh_auth_type: v as any }))}
              options={[
                { value: 'password', label: 'Password' },
                { value: 'key', label: 'SSH Key' },
              ]}
            />
          </div>
        </div>

        {config.ssh_auth_type === 'password' ? (
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>SSH Password</label>
            <Input
              type="password"
              value={config.ssh_password}
              onChange={(v) => setConfig(c => ({ ...c, ssh_password: v }))}
            />
          </div>
        ) : (
          <div style={{ marginBottom: 16 }}>
            <label style={labelStyle}>SSH Private Key</label>
            <Textarea
              rows={4}
              placeholder="-----BEGIN OPENSSH PRIVATE KEY-----"
              value={config.ssh_key}
              onInput={(e) => setConfig(c => ({ ...c, ssh_key: (e.target as HTMLTextAreaElement).value }))}
              style={{ ...inputStyle, fontFamily: 'monospace', fontSize: 12 }}
            />
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <label style={labelStyle}>Anthropic API Key (real, para o servidor ponte)</label>
          <Input
            type="password"
            placeholder="sk-ant-api03-..."
            value={config.anthropic_api_key}
            onChange={(v) => setConfig(c => ({ ...c, anthropic_api_key: v }))}
          />
          <span style={{ fontSize: 11, color: theme.dimmed }}>
            Esta chave será enviada ao servidor ponte via SSH deploy
          </span>
        </div>
      </div>

      {/* Actions */}
      <div style={{
        display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap',
      }}>
        <Button
          onClick={testHealth}
          disabled={!config.ip || testing !== null}
          loading={testing === 'health'}
        >
 {testing === 'health' ? 'Verificando...' : 'Verificar Health'}
        </Button>
        <Button
          variant="secondary"
          onClick={testSSH}
          disabled={!config.ip || testing !== null}
          loading={testing === 'ssh'}
        >
 {testing === 'ssh' ? 'Conectando...' : 'Testar SSH'}
        </Button>
        <Button
          onClick={deployRemote}
          disabled={!config.ip || testing !== null}
          loading={testing === 'deploy'}
        >
 {testing === 'deploy' ? 'Deployando...' : 'Deploy Remoto'}
        </Button>
        <Button
          variant="success"
          onClick={saveConfig}
          disabled={saving}
          loading={saving}
        >
 {saving ? 'Salvando...' : 'Salvar Config'}
        </Button>
      </div>

      {/* Status message */}
      {statusMsg && (
        <div style={{
          padding: '12px 16px',
          background: statusMsg.type === 'success' ? T.successBg : T.dangerBg,
          border: `1px solid ${statusMsg.type === 'success' ? theme.success : theme.error}`,
          borderRadius: 8,
          color: statusMsg.type === 'success' ? theme.success : theme.error,
          fontSize: 13,
        }}>
          {statusMsg.text}
        </div>
      )}

      {/* How it works */}
      <div style={{
        marginTop: 24, padding: '16px 20px',
        background: T.accentBg,
        border: `1px solid ${T.accentBg}`,
        borderRadius: 12,
      }}>
 <h4 style={{ margin: '0 0 12px', fontSize: 14, color: theme.accent }}>Como funciona</h4>
        <div style={{ fontSize: 12, color: theme.dimmed, lineHeight: 1.8 }}>
          <p style={{ margin: '0 0 8px' }}>
            <strong>1.</strong> Elon Router recebe request com OAuth token (sk-ant-oat01-*)
          </p>
          <p style={{ margin: '0 0 8px' }}>
            <strong>2.</strong> Detecta que bridge está habilitado → roteia para servidor ponte
          </p>
          <p style={{ margin: '0 0 8px' }}>
            <strong>3.</strong> Servidor ponte traduz: OAuth → API key real (sk-ant-api03-*)
          </p>
          <p style={{ margin: 0 }}>
            <strong>4.</strong> Chama Anthropic API com API key → retorna response transparente
          </p>
        </div>
      </div>
    </div>
  );
}
